import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  TextField,
  Button,
  Divider,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import LocalstorageHelper from "../../LocalStorageHelper/LocalstorageHelper";
import { Redirect } from "react-router-dom";
import Sidebar from "../Sidebar/Sidebar";

export default function CaseItemScanCopyDashboard() {
  const userToken = LocalstorageHelper.GetUserToken();

  const redirectToLoginPage = () => {
    if (!userToken) {
      return <Redirect exact to="/sparepartsLogin" />;
    }
  };

  const [invno, setInvno] = useState("");
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("items"); // items | dpoSummary | dpoItems | cases
  const [error, setError] = useState("");

  const [header, setHeader] = useState(null);
  const [items, setItems] = useState([]);
  const [dpoSummary, setDpoSummary] = useState([]);
  const [dpoItems, setDpoItems] = useState([]);
  const [cases, setCases] = useState([]);

  // reset pagination when tab changes (prevents "page out of range" weirdness)
  const [pageModel, setPageModel] = useState({ page: 0, pageSize: 25 });
  useEffect(() => {
    setPageModel((p) => ({ ...p, page: 0 }));
  }, [view]);

  const fmtNum = useCallback((v) => {
    const n = Number(v ?? 0);
    if (Number.isNaN(n)) return "0.00";
    return n.toFixed(2);
  }, []);

  const fetchDashboard = useCallback(async () => {
    const inv = (invno || "").trim();
    if (!inv) {
      setError("Please enter invno");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const r = await fetch(
        `/caseitemscancopy/dashboard?invno=${encodeURIComponent(inv)}`,
        {
          method: "GET",
          headers: {
            sptoken: userToken,
            "Content-Type": "application/json",
          },
        },
      );

      if (!r.ok) {
        const t = await r.text();
        throw new Error(t || "Failed to load dashboard");
      }

      const json = await r.json();
      setHeader(json.header || null);
      setItems(Array.isArray(json.items) ? json.items : []);
      setDpoSummary(Array.isArray(json.dpoSummary) ? json.dpoSummary : []);
      setDpoItems(Array.isArray(json.dpoItems) ? json.dpoItems : []);
      setCases(Array.isArray(json.cases) ? json.cases : []);
    } catch (e) {
      setHeader(null);
      setItems([]);
      setDpoSummary([]);
      setDpoItems([]);
      setCases([]);
      setError(e?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }, [invno, userToken]);

  const resetAll = useCallback(() => {
    setInvno("");
    setHeader(null);
    setItems([]);
    setDpoSummary([]);
    setDpoItems([]);
    setCases([]);
    setError("");
    setView("items");
    setPageModel({ page: 0, pageSize: 25 });
  }, []);

  /* ---------------- Columns ---------------- */
  const itemCols = useMemo(
    () => [
      { field: "partno", headerName: "Part No", width: 140 },
      {
        field: "partdescription",
        headerName: "Description",
        flex: 1,
        minWidth: 220,
      },
      {
        field: "transitqty",
        headerName: "Transit Qty",
        width: 120,
        type: "number",
        valueFormatter: (p) => fmtNum(p.value),
      },
      {
        field: "allocatedqty",
        headerName: "Allocated Qty (Scan)",
        width: 160,
        type: "number",
        valueFormatter: (p) => fmtNum(p.value),
      },
      {
        field: "dpo_alloc_qty",
        headerName: "DPO Alloc",
        width: 120,
        type: "number",
        valueFormatter: (p) => fmtNum(p.value),
      },
      {
        field: "stock_alloc_qty",
        headerName: "Stock Alloc",
        width: 130,
        type: "number",
        valueFormatter: (p) => fmtNum(p.value),
      },
      {
        field: "balanceqty",
        headerName: "Balance",
        width: 120,
        type: "number",
        valueFormatter: (p) => fmtNum(p.value),
      },
      {
        field: "status",
        headerName: "Status",
        width: 150,
        sortable: false,
        filterable: false,
        renderCell: (p) => {
          const bal = Number(p.row?.balanceqty ?? 0);
          if (bal === 0)
            return (
              <Chip
                size="small"
                label="MATCHED"
                color="success"
                variant="outlined"
              />
            );
          if (bal > 0)
            return (
              <Chip
                size="small"
                label="SHORT"
                color="warning"
                variant="outlined"
              />
            );
          return (
            <Chip
              size="small"
              label="EXCESS"
              color="error"
              variant="outlined"
            />
          );
        },
      },
    ],
    [fmtNum],
  );

  const dpoSummaryCols = useMemo(
    () => [
      { field: "dpono", headerName: "DPO No", width: 140 },
      {
        field: "party",
        headerName: "Party",
        flex: 1,
        minWidth: 260,
        valueGetter: (p) => {
          if ((p.row?.dpono || "") === "STOCK") return "STOCK";
          const code = (p.row?.partycode || "").trim();
          const name = (p.row?.partyname || "").trim();
          if (code && name) return `${code} - ${name}`;
          if (name) return name;
          if (code) return code;
          return `Subledger: ${p.row?.subledgerid ?? ""}`;
        },
      },
      {
        field: "dpo_qty",
        headerName: "DPO Qty",
        width: 120,
        type: "number",
        valueFormatter: (p) => fmtNum(p.value),
      },
      {
        field: "alloc_qty",
        headerName: "Allocated",
        width: 120,
        type: "number",
        valueFormatter: (p) => fmtNum(p.value),
      },
      {
        field: "unalloc_qty",
        headerName: "Unallocated",
        width: 130,
        type: "number",
        valueFormatter: (p) => fmtNum(p.value),
      },
      {
        field: "status",
        headerName: "Status",
        width: 150,
        sortable: false,
        filterable: false,
        renderCell: (p) => {
          const u = Number(p.row?.unalloc_qty ?? 0);
          if (u > 0)
            return (
              <Chip
                size="small"
                label="UNALLOCATED"
                color="warning"
                variant="outlined"
              />
            );
          return (
            <Chip size="small" label="OK" color="success" variant="outlined" />
          );
        },
      },
    ],
    [fmtNum],
  );

  const dpoItemCols = useMemo(
    () => [
      { field: "dpono", headerName: "DPO No", width: 140 },
      { field: "partno", headerName: "Part No", width: 140 },
      {
        field: "partdescription",
        headerName: "Description",
        flex: 1,
        minWidth: 240,
      },
      {
        field: "dpo_qty",
        headerName: "DPO Qty",
        width: 120,
        type: "number",
        valueFormatter: (p) => fmtNum(p.value),
      },
      {
        field: "alloc_qty",
        headerName: "Allocated",
        width: 120,
        type: "number",
        valueFormatter: (p) => fmtNum(p.value),
      },
      {
        field: "unalloc_qty",
        headerName: "Unallocated",
        width: 130,
        type: "number",
        valueFormatter: (p) => fmtNum(p.value),
      },
    ],
    [fmtNum],
  );

  const caseCols = useMemo(
    () => [
      { field: "casenumber", headerName: "Case No", flex: 1, minWidth: 220 },
      {
        field: "billty_display",
        headerName: "Billty / Verification",
        flex: 1,
        minWidth: 340,
      },
      {
        field: "verified_at",
        headerName: "Verified At",
        width: 200,
        valueGetter: (p) => p.row?.verified_at || "",
      },
    ],
    [],
  );

  const activeRows = useMemo(() => {
    if (view === "items") return items;
    if (view === "dpoSummary") return dpoSummary;
    if (view === "dpoItems") return dpoItems;
    return cases;
  }, [view, items, dpoSummary, dpoItems, cases]);

  const activeCols = useMemo(() => {
    if (view === "items") return itemCols;
    if (view === "dpoSummary") return dpoSummaryCols;
    if (view === "dpoItems") return dpoItemCols;
    return caseCols;
  }, [view, itemCols, dpoSummaryCols, dpoItemCols, caseCols]);

  // ✅ stable deterministic row id for each view (NO Math.random)
  const gridRowId = useCallback(
    (row) => {
      if (view === "items") {
        // safest: itemid + partno (handles duplicate partno edge cases)
        return `ITM|${row.itemid ?? ""}|${row.partno ?? ""}`;
      }
      if (view === "dpoSummary") {
        return `DPOSUM|${row.dpono ?? ""}|${row.subledgerid ?? 0}`;
      }
      if (view === "dpoItems") {
        return `DPOITM|${row.dpono ?? ""}|${row.subledgerid ?? 0}|${row.itemid ?? 0}|${row.partno ?? ""}`;
      }
      // cases: casenumber + invno if available, else casenumber
      return `CASE|${row.casenumber ?? ""}|${row.invno ?? row.invoicenumber ?? ""}`;
    },
    [view],
  );

  const totalTransit = Number(header?.transit_total_qty ?? 0);
  const totalAlloc = Number(header?.allocated_total_qty ?? 0);
  const totalBal = totalTransit - totalAlloc;
  const totalDpoAlloc = Number(header?.dpo_alloc_total ?? 0);
  const totalStockAlloc = Number(header?.stock_alloc_total ?? 0);

  return (
    <div>
      <Sidebar
        Title={"Case Item Scan Copy Dashboard"}
        expandedPanel={"panel2"}
        children={
          <Box p={2}>
            <Stack spacing={2}>
              {/* Search Card */}
              <Card>
                <CardContent>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    alignItems="center"
                  >
                    <TextField
                      label="Transit Invoice No (invno)"
                      value={invno}
                      onChange={(e) => setInvno(e.target.value)}
                      fullWidth
                      size="small"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") fetchDashboard();
                      }}
                    />
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        startIcon={
                          loading ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <SearchIcon />
                          )
                        }
                        onClick={fetchDashboard}
                        disabled={loading}
                      >
                        Search
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={resetAll}
                        disabled={loading}
                      >
                        Reset
                      </Button>
                    </Stack>
                  </Stack>

                  {error ? (
                    <Typography mt={1} color="error" variant="body2">
                      {error}
                    </Typography>
                  ) : null}

                  {/* Header Summary */}
                  {header ? (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                      >
                        <Chip
                          label={`INVNO: ${header.invno || ""}`}
                          variant="outlined"
                        />
                        <Chip
                          label={`Inv Date: ${header.invdate || "-"}`}
                          variant="outlined"
                        />
                        <Chip
                          label={`Transit Qty: ${fmtNum(totalTransit)}`}
                          variant="outlined"
                        />
                        <Chip
                          label={`Allocated Qty (Scan): ${fmtNum(totalAlloc)}`}
                          variant="outlined"
                        />
                        <Chip
                          label={`DPO Alloc: ${fmtNum(totalDpoAlloc)}`}
                          variant="outlined"
                        />
                        <Chip
                          label={`Stock Alloc: ${fmtNum(totalStockAlloc)}`}
                          variant="outlined"
                        />
                        <Chip
                          label={`Balance: ${fmtNum(totalBal)}`}
                          color={totalBal === 0 ? "success" : "warning"}
                          variant="outlined"
                        />
                        <Chip
                          label={`Transit Items: ${header.transit_item_count || 0}`}
                          variant="outlined"
                        />
                        <Chip
                          label={`Allocated Items: ${header.allocated_item_count || 0}`}
                          variant="outlined"
                        />
                        <Chip
                          label={`Cases: ${header.case_count || 0}`}
                          variant="outlined"
                        />
                      </Stack>
                    </>
                  ) : null}
                </CardContent>
              </Card>

              {/* View Toggle */}
              <Card>
                <CardContent>
                  <Stack spacing={1}>
                    <Typography variant="subtitle2">
                      View (invno-based. Allocated Qty comes from
                      t_caseitemscan_copy.allocqty. No pickqty.)
                    </Typography>
                    <ToggleButtonGroup
                      value={view}
                      exclusive
                      onChange={(e, v) => v && setView(v)}
                      size="small"
                    >
                      <ToggleButton value="items">Item Summary</ToggleButton>
                      <ToggleButton value="dpoSummary">
                        DPO Summary
                      </ToggleButton>
                      <ToggleButton value="dpoItems">DPO Items</ToggleButton>
                      <ToggleButton value="cases">
                        Case Verification
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                </CardContent>
              </Card>

              {/* Grid */}
              <Card>
                <CardContent>
                  <Box sx={{ height: 560, width: "100%" }}>
                    <DataGrid
                      // ✅ KEY forces a clean remount when view changes (stops tab glitch)
                      key={view}
                      rows={activeRows}
                      columns={activeCols}
                      getRowId={gridRowId}
                      loading={loading}
                      slots={{ toolbar: GridToolbar }}
                      slotProps={{
                        toolbar: {
                          showQuickFilter: true,
                          quickFilterProps: { debounceMs: 250 },
                        },
                      }}
                      disableRowSelectionOnClick
                      density="compact"
                      pageSizeOptions={[10, 25, 50, 100]}
                      paginationModel={pageModel}
                      onPaginationModelChange={setPageModel}
                      initialState={{
                        pagination: {
                          paginationModel: { pageSize: 25, page: 0 },
                        },
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Stack>
          </Box>
        }
      />
      {redirectToLoginPage()}
    </div>
  );
}
